import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Drawer, Dropdown, Input, Segmented, Select, Spin } from 'antd';
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  FileZipOutlined,
  MailOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import dynamicFormsAdminService from '../../services/dynamicFormsAdminService';
import { API_ORIGIN } from '../../config';
import { openApiFile } from '../../utils/apiAuth';
import useElementWidth from './useElementWidth';
import { formatFieldName, questionTitle, truthy } from './fieldTypes';

const PAGE = 50;

const isFile = (v) => v && typeof v === 'object' && !Array.isArray(v) && v.file_name && v.download_url;
const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

export const fmtWhen = (value) => {
  if (!value) return '';
  const d = dayjs(value);
  const today = dayjs();
  if (d.isSame(today, 'day')) return `Today, ${d.format('HH:mm')}`;
  if (d.isSame(today.subtract(1, 'day'), 'day')) return `Yesterday, ${d.format('HH:mm')}`;
  return d.isSame(today, 'year') ? d.format('D MMM, HH:mm') : d.format('D MMM YYYY');
};

const fmtLong = (value) => (value ? dayjs(value).format('D MMMM YYYY [at] HH:mm') : '');

const text = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v));

/**
 * Who sent a response, from the usual field names (full_name, first_name + last_name, email…).
 * Falls back to the first short answer, then to the response number.
 */
const identify = (row, fields) => {
  const byName = (re) => fields.find((f) => re.test(f.field_name) && text(row[f.field_name]));
  const full = byName(/^(full_?name|name|applicant_?name|name_?(and_)?surname|your_?name)$/i);
  const first = byName(/(^|_)(first_?name|name_first|forename|given_?name)$/i);
  const last = byName(/(^|_)(last_?name|surname|family_?name)$/i);
  const email = fields.find((f) => /e_?mail/i.test(f.field_name) && text(row[f.field_name]));
  let name = full ? text(row[full.field_name]) : [first && text(row[first.field_name]), last && text(row[last.field_name])].filter(Boolean).join(' ');
  if (!name) {
    const org = byName(/(organi[sz]ation|company|band|artist|group|entity)(_?name)?$/i);
    if (org) name = text(row[org.field_name]);
  }
  if (!name) {
    const short = fields.find((f) => f.field_type === 'varchar' && f !== email && text(row[f.field_name]));
    if (short) name = text(row[short.field_name]);
  }
  return { name: name || `Response #${row.submission_id}`, email: email ? text(row[email.field_name]) : '' };
};

const haystackOf = (row) =>
  Object.values(row)
    .map((v) => (isFile(v) ? v.file_name : Array.isArray(v) ? v.join(' ') : v == null ? '' : String(v)))
    .join(' ')
    .toLowerCase();

const download = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const Answer = ({ field, value, onOpenFile }) => {
  if (isEmpty(value)) return <span className="dfw-answer__none">No answer</span>;
  if (isFile(value)) {
    return (
      <span className="dfw-file">
        <PaperClipOutlined aria-hidden="true" />
        <span className="dfw-file__name">{value.file_name}</span>
        <Button size="small" icon={<DownloadOutlined aria-hidden="true" />} onClick={() => onOpenFile(value)}>Download</Button>
      </span>
    );
  }
  if (Array.isArray(value)) {
    return (
      <ul className="dfw-chips">
        {value.map((v, i) => <li key={i}>{String(v)}</li>)}
      </ul>
    );
  }
  if (field?.field_type === 'boolean' || typeof value === 'boolean') return truthy(value) ? 'Yes' : 'No';
  if (field?.field_type === 'date') return dayjs(value).isValid() ? dayjs(value).format('D MMMM YYYY') : String(value);
  return <span className="dfw-answer__text">{String(value)}</span>;
};

/**
 * Every response to the form, from the export endpoint (all answers, all rows), with search
 * across every answer and filters for workshop options and attachments. No charts: the count
 * and the latest response are plain text.
 */
const ResponsesView = ({ formId, formName, fields, responses, loading, onReload, canDelete }) => {
  const { message, modal } = App.useApp();
  const rootRef = useRef(null);
  const width = useElementWidth(rootRef);
  const [query, setQuery] = useState('');
  const [workshop, setWorkshop] = useState(null);
  const [filesOnly, setFilesOnly] = useState(false);
  const [order, setOrder] = useState('newest');
  const [shown, setShown] = useState(PAGE);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(null);

  const rows = useMemo(() => responses?.data || [], [responses]);
  const sortedFields = useMemo(() => [...fields].sort((a, b) => a.field_order - b.field_order), [fields]);
  const workshopFields = useMemo(() => sortedFields.filter((f) => truthy(f.is_workshop)), [sortedFields]);
  const fileFields = useMemo(() => sortedFields.filter((f) => f.field_type === 'file'), [sortedFields]);
  const mainWorkshop = workshopFields[0];

  const enriched = useMemo(
    () => rows.map((row) => ({ row, who: identify(row, sortedFields), hay: haystackOf(row), files: fileFields.some((f) => isFile(row[f.field_name])) })),
    [rows, sortedFields, fileFields]
  );

  const workshopOptions = useMemo(() => {
    const opts = [];
    workshopFields.forEach((f) => {
      (f.field_options || []).forEach((label) => opts.push({ value: `${f.field_name}::${label}`, label: workshopFields.length > 1 ? `${questionTitle(f)}: ${label}` : label }));
    });
    return opts;
  }, [workshopFields]);

  const term = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    let list = enriched;
    if (term) list = list.filter((e) => e.hay.includes(term));
    if (workshop) {
      const [name, label] = workshop.split('::');
      list = list.filter((e) => {
        const v = e.row[name];
        return Array.isArray(v) ? v.includes(label) : v === label;
      });
    }
    if (filesOnly) list = list.filter((e) => e.files);
    const sign = order === 'newest' ? -1 : 1;
    return [...list].sort((a, b) => sign * (new Date(a.row.submitted_at) - new Date(b.row.submitted_at)) || sign * (a.row.submission_id - b.row.submission_id));
  }, [enriched, term, workshop, filesOnly, order]);

  useEffect(() => { setShown(PAGE); }, [term, workshop, filesOnly, order]);

  const open = enriched.find((e) => e.row.submission_id === openId) || null;
  const latest = rows.reduce((max, r) => (!max || new Date(r.submitted_at) > new Date(max) ? r.submitted_at : max), null);
  const total = responses?.total ?? rows.length;
  const split = width >= 980;

  // answers to questions that were deleted since are not in the export, so only live fields show
  const answerFields = sortedFields;

  const openFile = async (value) => {
    try {
      await openApiFile(`${API_ORIGIN}${value.download_url}`, { download: true, filename: value.file_name });
    } catch (err) {
      message.error(err.message || 'Could not download the file');
    }
  };

  const downloadAll = async (row) => {
    try {
      setBusy('zip');
      const blob = await dynamicFormsAdminService.downloadAllAttachments(row.submission_id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `submission_${row.submission_id}_attachments.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message || 'Could not download the files');
    } finally {
      setBusy(null);
    }
  };

  const resend = (row, who) => {
    modal.confirm({
      title: 'Send the emails again?',
      content: `The confirmation to ${who.email || 'the applicant'}, the review request and the internal notice for response #${row.submission_id} are sent again, as set up under Emails.`,
      okText: 'Send emails',
      rootClassName: 'do-scope',
      onOk: async () => {
        try {
          const result = await dynamicFormsAdminService.resendSubmissionEmails(row.submission_id);
          if (result.errors?.length) message.warning(result.message || 'Some emails were not sent');
          else message.success(result.message || 'Emails sent');
        } catch (err) {
          message.error(err.message || 'Could not send the emails');
        }
      },
    });
  };

  const remove = (row, who) => {
    modal.confirm({
      title: `Delete response #${row.submission_id}?`,
      content: `${who.name}'s response is removed from this list and from exports, and any workshop places it held are freed.`,
      okText: 'Delete response',
      okButtonProps: { danger: true },
      rootClassName: 'do-scope',
      onOk: async () => {
        try {
          const result = await dynamicFormsAdminService.deleteSubmission(formId, row.submission_id);
          if (!result.success) throw new Error(result.error);
          message.success('Response deleted');
          setOpenId(null);
          onReload();
        } catch (err) {
          message.error(err.message || 'Could not delete the response');
        }
      },
    });
  };

  const exportCsv = async (key) => {
    const safe = (formName || 'form').replace(/[^a-z0-9]/gi, '_');
    const day = dayjs().format('YYYY-MM-DD');
    try {
      setBusy('export');
      if (key === 'all') {
        const res = await dynamicFormsAdminService.exportSubmissionsCsv(formId);
        download(res.csvContent, `${safe}_responses_${day}.csv`);
      } else {
        const res = await dynamicFormsAdminService.exportSubmissionsGroupedByWorkshop(formId, key);
        download(res.csvContent, `${safe}_${key}_by_workshop_${day}.csv`);
      }
      message.success('CSV downloaded');
    } catch (err) {
      message.error(err.message || 'Could not export the responses');
    } finally {
      setBusy(null);
    }
  };

  const exportItems = [
    { key: 'all', label: 'All responses (CSV)' },
    ...workshopFields.map((f) => ({ key: f.field_name, label: `Grouped by ${questionTitle(f)} (CSV)` })),
  ];

  const detail = open && (
    <article className="dfw-detail" aria-labelledby="dfw-detail-title">
      <header className="dfw-detail__head">
        <div>
          <h3 className="dfw-detail__title" id="dfw-detail-title">{open.who.name}</h3>
          <p className="dfw-detail__meta">Response #{open.row.submission_id} · {fmtLong(open.row.submitted_at)}</p>
        </div>
        {split && (
          <Button type="text" icon={<CloseOutlined aria-hidden="true" />} onClick={() => setOpenId(null)} aria-label="Close response" />
        )}
      </header>
      <dl className="dfw-answers">
        {answerFields.map((f) => (
          <div className="dfw-answers__row" key={f.field_id}>
            <dt>{f.display_label || formatFieldName(f.field_name)}</dt>
            <dd><Answer field={f} value={open.row[f.field_name]} onOpenFile={openFile} /></dd>
          </div>
        ))}
      </dl>
      <footer className="dfw-detail__actions">
        {open.files && (
          <Button icon={<FileZipOutlined aria-hidden="true" />} onClick={() => downloadAll(open.row)} loading={busy === 'zip'}>
            Download all files
          </Button>
        )}
        <Button icon={<MailOutlined aria-hidden="true" />} onClick={() => resend(open.row, open.who)}>Resend emails</Button>
        <span className="dfw-grow" />
        {canDelete && (
          <Button danger type="text" icon={<DeleteOutlined aria-hidden="true" />} onClick={() => remove(open.row, open.who)}>Delete</Button>
        )}
      </footer>
    </article>
  );

  return (
    <div className="dfw-responses" ref={rootRef}>
      <div className="dfw-builder__bar">
        <p className="dfw-builder__lede">
          {total === 0 ? 'No responses yet.' : `${total.toLocaleString()} ${total === 1 ? 'response' : 'responses'}`}
          {latest && <> · the latest {fmtWhen(latest).replace(/^Today/, 'today').replace(/^Yesterday/, 'yesterday')}</>}
        </p>
        <span className="dfw-grow" />
        <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={onReload} loading={loading}>Refresh</Button>
        <Dropdown
          menu={{ items: exportItems, onClick: ({ key }) => exportCsv(key) }}
          trigger={['click']}
          disabled={total === 0}
          rootClassName="do-scope"
        >
          <Button icon={<DownloadOutlined aria-hidden="true" />} loading={busy === 'export'}>
            Export <DownOutlined aria-hidden="true" />
          </Button>
        </Dropdown>
      </div>

      <div className="dfw-filters">
        <Input
          className="dfw-search"
          allowClear
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder="Search names, emails or any answer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search responses"
        />
        {workshopOptions.length > 0 && (
          <Select
            className="dfw-filter"
            allowClear
            placeholder="All options"
            value={workshop}
            onChange={setWorkshop}
            options={workshopOptions}
            popupMatchSelectWidth={false}
            aria-label="Filter by workshop"
          />
        )}
        {fileFields.length > 0 && (
          <button type="button" className={`dfw-chip${filesOnly ? ' is-on' : ''}`} aria-pressed={filesOnly} onClick={() => setFilesOnly((v) => !v)}>
            <PaperClipOutlined aria-hidden="true" /> Has files
          </button>
        )}
        <span className="dfw-grow" />
        <Segmented
          size="small"
          value={order}
          onChange={setOrder}
          options={[{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }]}
          aria-label="Order"
        />
      </div>

      <div className={`dfw-responses__body${split && open ? ' is-split' : ''}`}>
        <div className="dfw-responses__list">
          {loading && rows.length === 0 ? (
            <div className="dfw-empty"><Spin /></div>
          ) : filtered.length === 0 ? (
            <div className="dfw-empty">
              <strong>{rows.length === 0 ? 'No responses yet' : 'No responses match'}</strong>
              <span>{rows.length === 0 ? 'Responses appear here as soon as people send the form.' : 'Try another search or clear the filters.'}</span>
            </div>
          ) : (
            <>
              <div className={`dfw-rtable${mainWorkshop ? ' has-workshop' : ''}`} role="table" aria-label="Responses">
                <div className="dfw-rtable__head" role="row">
                  <span role="columnheader">#</span>
                  <span role="columnheader">Applicant</span>
                  {mainWorkshop && <span role="columnheader">{questionTitle(mainWorkshop)}</span>}
                  <span role="columnheader">Received</span>
                </div>
                {filtered.slice(0, shown).map(({ row, who, files }) => {
                  const pick = mainWorkshop ? row[mainWorkshop.field_name] : null;
                  return (
                    <button
                      type="button"
                      role="row"
                      key={row.submission_id}
                      className={`dfw-rtable__row${row.submission_id === openId ? ' is-on' : ''}`}
                      onClick={() => setOpenId(row.submission_id === openId ? null : row.submission_id)}
                      aria-expanded={row.submission_id === openId}
                    >
                      <span role="cell" className="dfw-num dfw-rtable__id">{row.submission_id}</span>
                      <span role="cell" className="dfw-rtable__who">
                        <strong>{who.name}{files && <PaperClipOutlined className="dfw-rtable__clip" aria-label="has files" />}</strong>
                        {who.email && <small>{who.email}</small>}
                      </span>
                      {mainWorkshop && (
                        <span role="cell" className="dfw-rtable__pick">
                          {isEmpty(pick) ? <span className="dfw-answer__none">None</span> : Array.isArray(pick) ? pick.join(', ') : String(pick)}
                        </span>
                      )}
                      <span role="cell" className="dfw-rtable__when">{fmtWhen(row.submitted_at)}</span>
                    </button>
                  );
                })}
              </div>
              {filtered.length > shown && (
                <Button className="dfw-more" type="link" onClick={() => setShown((n) => n + PAGE)}>
                  Show {Math.min(PAGE, filtered.length - shown)} more of {filtered.length - shown}
                </Button>
              )}
              {(term || workshop || filesOnly) && (
                <p className="dfw-count">{filtered.length} of {rows.length} responses</p>
              )}
            </>
          )}
        </div>
        {split && open && <div className="dfw-responses__detail">{detail}</div>}
      </div>

      {!split && (
        <Drawer
          open={!!open}
          onClose={() => setOpenId(null)}
          width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
          title={open ? `Response #${open.row.submission_id}` : ''}
          rootClassName="do-scope dfw-drawer"
        >
          {detail}
        </Drawer>
      )}
    </div>
  );
};

export default ResponsesView;
